import { useEffect, useMemo, useState } from 'react';
import { chunkOfId } from '@versesverse/core';
import type { VerseChunkArtifact } from '@versesverse/schema/artifacts';
import { buildPassages } from './passages';
import { makeTextOf } from './load';
import { contextWindow, unitResolver } from './context';
import type { VerseSource, VerseTables, VersesState } from './useNodeVerses';

/*
 * READING THE PASSAGE AROUND ONE VERSE.
 *
 * Where `useNodeVerses` loads the scattered verses that name a figure, this loads a CONTIGUOUS run
 * — the verse a reader opened and its neighbours up to the unit boundary — for the expanded reader.
 * Same shape of work: resolve the ids (here from `contextWindow`, not a name list), fetch the ranges
 * that hold them, shape them with the same builder so they read identically to the drawer's verses,
 * clickable names and all. `span` verses each side, widened on demand.
 */
export interface ContextState extends VersesState {
  /** The passage heading — the unit both ends stopped at (e.g. "Genesis 1", "Al-Baqarah"). */
  unit: string;
  /** The verse the reader opened, so the drawer can mark it among its neighbours. */
  centerId: number | null;
}

interface Loaded {
  center: number;
  ids: number[];
  chunks: Map<number, VerseChunkArtifact>;
  incomplete: boolean;
}

export function useContextVerses(source: VerseSource, tables: VerseTables, center: number | null, before: number, after: number): ContextState {
  const [loaded, setLoaded] = useState<Loaded | null>(null);

  const ids = useMemo(() => (center == null ? [] : contextWindow(center, before, after, tables)), [center, before, after, tables]);
  const unit = useMemo(() => (center == null ? '' : unitResolver(tables)(center)), [center, tables]);

  useEffect(() => {
    if (center == null) { setLoaded(null); return; }
    let live = true;
    /* Blank the reader ONLY when the passage itself changes (a new centre). Growing one side — the
       reader scrolling to load more of the same passage — keeps the current verses on screen and fills
       the new ones in when they arrive, so scroll-to-read-more never flashes empty. */
    setLoaded(prev => (prev && prev.center === center) ? prev : null);
    void (async () => {
      const need = [...new Set(ids.map(id => chunkOfId(tables.chunkStarts, id)))];
      const chunks = new Map<number, VerseChunkArtifact>();
      let incomplete = false;
      await Promise.all(need.map(async chunk => {
        try { chunks.set(chunk, await source.verseChunk(chunk)); } catch { incomplete = true; }
      }));
      if (live) setLoaded({ center, ids, chunks, incomplete });
    })();
    return () => { live = false; };
  }, [center, before, after]); // eslint-disable-line react-hooks/exhaustive-deps -- source/tables stable; ids derive from center+before+after

  return useMemo<ContextState>(() => {
    if (center == null) return { status: 'idle', passages: [], incomplete: false, expected: 0, unit: '', centerId: null };
    /* Ready as soon as SOME window for this centre has loaded — a wider span still filling in keeps
       showing what it has, so the reader stays populated as it grows. */
    if (!loaded || loaded.center !== center) {
      return { status: 'loading', passages: [], incomplete: false, expected: 0, unit, centerId: center };
    }
    const textOf = makeTextOf(loaded.chunks, tables.chunkStarts);
    return { status: 'ready', passages: buildPassages(loaded.ids, textOf, tables), incomplete: loaded.incomplete, expected: loaded.ids.length, unit, centerId: center };
  }, [center, loaded, tables, unit]);
}
